/**
 * Pure, dependency-free helpers for exporting the project activity
 * (audit) log as CSV.
 *
 * These run on entries returned by the `exportAuditLogsV2` /
 * `getAuditLogsV2` callables. Over the callable wire a Firestore Admin
 * `Timestamp` is serialised to a plain `{ _seconds, _nanoseconds }`
 * object — it does NOT carry the client SDK's `.toDate()` method — so
 * `auditTimestampToDate` normalises every shape we might receive
 * (client Timestamp, callable-serialised Timestamp, ISO string, epoch
 * millis) to a `Date`.
 *
 * Kept free of React / Firebase imports so the logic is unit-testable
 * in isolation.
 */

export interface AuditCsvEntry {
  action: string;
  resourceType?: string;
  resourceId?: string;
  userName?: string;
  userEmail?: string;
  description: string;
  timestamp?: unknown;
}

/** Normalise any timestamp shape we may receive to a `Date`, or `null`. */
export function auditTimestampToDate(ts: unknown): Date | null {
  if (ts == null) return null;

  // Client Firestore Timestamp — has a live toDate().
  const maybe = ts as { toDate?: () => unknown };
  if (typeof maybe.toDate === 'function') {
    try {
      const d = maybe.toDate();
      return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }

  // Callable-serialised Timestamp: {_seconds,_nanoseconds} or {seconds,nanoseconds}.
  if (typeof ts === 'object') {
    const o = ts as Record<string, unknown>;
    const secs =
      typeof o._seconds === 'number'
        ? o._seconds
        : typeof o.seconds === 'number'
        ? o.seconds
        : null;
    if (secs == null) return null;
    const nanos =
      typeof o._nanoseconds === 'number'
        ? o._nanoseconds
        : typeof o.nanoseconds === 'number'
        ? o.nanoseconds
        : 0;
    const d = new Date(secs * 1000 + Math.floor(nanos / 1e6));
    return isNaN(d.getTime()) ? null : d;
  }

  // Epoch millis or an ISO/parseable date string.
  if (typeof ts === 'number' || typeof ts === 'string') {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
}

/**
 * Append a freshly-fetched page of audit entries to the ones already
 * loaded, de-duplicating by `id`. The pagination cursor sends the oldest
 * loaded entry's timestamp as an *inclusive* `endDate`, so that boundary
 * entry comes back again on the next page — this drops the repeat while
 * preserving newest-first order.
 */
export function mergeAuditPages<T extends { id: string }>(
  existing: T[],
  incoming: T[]
): T[] {
  if (incoming.length === 0) return existing;
  const seen = new Set(existing.map(e => e.id));
  const fresh = incoming.filter(e => !seen.has(e.id));
  return fresh.length === 0 ? existing : [...existing, ...fresh];
}

const CSV_HEADER = [
  'Timestamp',
  'User name',
  'User email',
  'Action',
  'Resource type',
  'Resource ID',
  'Description',
];

/** RFC-4180 field escaping: quote + double inner quotes when needed. */
function escapeCsv(field: string): string {
  return /[",\r\n]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field;
}

/**
 * Serialise audit entries to an RFC-4180 CSV string (with header row).
 * Entries are emitted in the order given. Timestamps render as ISO-8601
 * (UTC), which sorts cleanly and is unambiguous for a compliance export.
 */
export function auditLogsToCsv(entries: AuditCsvEntry[]): string {
  const rows = [CSV_HEADER.join(',')];
  for (const e of entries) {
    const date = auditTimestampToDate(e.timestamp);
    const cells = [
      date ? date.toISOString() : '',
      e.userName ?? '',
      e.userEmail ?? '',
      e.action ?? '',
      e.resourceType ?? '',
      e.resourceId ?? '',
      e.description ?? '',
    ];
    rows.push(cells.map(c => escapeCsv(String(c))).join(','));
  }
  return rows.join('\r\n') + '\r\n';
}
